/**
 * User settings that persist per browser (Sprint 2, S2-16): rendering quality, sound, cinematic camera and reduced
 * effects. Stored in localStorage when available — every access is guarded (private mode, blocked storage).
 */
import { type Readable, derived, readable, writable } from 'svelte/store';
import { type Quality, isQuality } from '../core/quality';
import { type LaneGrouping, isLaneGrouping } from '../core/timelineLanes';

export type QualityPreference = 'auto' | Quality;

const KEY_QUALITY = 'zeitflug.quality';

function load(key: string): string | null {
  try {
    return globalThis.localStorage?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

function save(key: string, value: string): void {
  try {
    globalThis.localStorage?.setItem(key, value);
  } catch {
    // storage unavailable: the setting lasts for this session only
  }
}

const stored = load(KEY_QUALITY);
/** Quality preference: 'auto' lets the frame-time monitor choose (D5). */
export const qualityPref = writable<QualityPreference>(isQuality(stored) ? stored : 'auto');

let persist = false; // the first subscription call only echoes the loaded value
qualityPref.subscribe((q) => {
  if (persist) save(KEY_QUALITY, q);
});
persist = true;

/** Applies a quality from a deep link for this session only (not persisted). */
export function overrideQuality(q: Quality): void {
  persist = false;
  qualityPref.set(q);
  persist = true;
}

const KEY_LANES = 'zeitflug.timelineLanes';
const storedLanes = load(KEY_LANES);
/** How the timeline groups its lanes (all missions packed, by agency, by destination). */
export const laneGrouping = writable<LaneGrouping>(isLaneGrouping(storedLanes) ? storedLanes : 'none');
let persistLanes = false;
laneGrouping.subscribe((g) => {
  if (persistLanes) save(KEY_LANES, g);
});
persistLanes = true;

const KEY_INTRO_SEEN = 'zeitflug.introSeen';

/** Whether this browser has seen the intro already (it plays once, S2-12). */
export function introSeen(): boolean {
  return load(KEY_INTRO_SEEN) === '1';
}

export function markIntroSeen(): void {
  save(KEY_INTRO_SEEN, '1');
}

/** A boolean setting persisted under `key` ('1' / '0'). */
function persistedBool(key: string, fallback: boolean) {
  const raw = load(key);
  const store = writable(raw === null ? fallback : raw === '1');
  let first = true;
  store.subscribe((v) => {
    if (!first) save(key, v ? '1' : '0');
    first = false;
  });
  return store;
}

/** Sound cues (S2-14): off by default (D7). */
export const soundOn = persistedBool('zeitflug.sound', false);
/** Automatic camera framing during flybys (S2-11). */
export const cinematicCamera = persistedBool('zeitflug.cinematic', true);
/** Reduce motion and effects regardless of the OS setting (S2-16). */
export const reducedEffects = persistedBool('zeitflug.reducedEffects', false);
/** Hide the HUD after a few seconds without input while time plays (S4-15). */
export const autoHideHud = persistedBool('zeitflug.autoHide', true);

/** The OS-level `prefers-reduced-motion` as a store. */
const prefersReducedMotion: Readable<boolean> = readable(false, (set) => {
  const mq = globalThis.matchMedia?.('(prefers-reduced-motion: reduce)');
  if (!mq) return;
  set(mq.matches);
  const on = (): void => set(mq.matches);
  mq.addEventListener('change', on);
  return () => mq.removeEventListener('change', on);
});

/** Motion is reduced when the OS asks for it or the user chose "reduce effects" (D9). */
export const motionReduced = derived([prefersReducedMotion, reducedEffects], ([os, user]) => os || user);
